import { PermissionsService } from '../../../../../services/core/permissions.service';
import { Component, ChangeDetectionStrategy, inject, signal, computed, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';

import { ResumeDataService } from '../../services/resume-data.service';
import { AgreementService } from '../../../../../services/customers/agreement.service';
import { ClientsService } from '../../../../../services/customers/clients.service';
import { DialogsService } from '../../../../../services/dialogs.service';
import { CustomerTypesEnum, AgreementStatusEnum, AgreementTypeEnum } from '../../../../../constants/customers.enum';
import { SharedModule } from '../../../../../layout/shared/shared.module';
import { DirectivesModule } from '../../../../../directives/directives.module';
import { KiteModule } from '../../../../../shared/widgets/kite/kite.module';
import { WidgetsModule } from '../../../../../shared/widgets.module';
import { GlobalPresenter } from '../../../../../services/global/presenter/global.presenter';
import { ActivatedRoute, Router } from '@angular/router';
import { ResumeClient, LastUpdatedBy } from '../../resume.model';
import { ReconciliationTypesEnum } from '../../../../../constants/master-lists';

export enum ClientTypeEnum {
  ECO = 'eco',
  UCO = 'uco'
}

@Component({
    selector: 'app-basic-info-tab',
    imports: [CommonModule, NgbTooltipModule, SharedModule, DirectivesModule, KiteModule, WidgetsModule, ReactiveFormsModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './basic-info-tab.component.html',
    styleUrls: ['./basic-info-tab.component.scss']
})
export class BasicInfoTabComponent {

  readonly resumeData = inject(ResumeDataService);
  readonly permissions = inject(PermissionsService);
  private readonly agreementService = inject(AgreementService);
  private readonly modalsService = inject(DialogsService);
  private readonly toastr = inject(ToastrService);
  private readonly clientsService = inject(ClientsService);
  private readonly route = inject(ActivatedRoute);
  readonly CustomerTypesEnum = CustomerTypesEnum;
  readonly loadingServiceAgreement = signal(false);
  readonly editClientMode = signal(false);
  readonly loadingEditClient = signal(false);

  private readonly router = inject(Router);

  /** Returns the enriched master_agreement object, or null if not present / still a legacy string. */
  readonly masterAgreementData = computed(() => {
    const ma = this.resumeData.clientInfomationTabData()?.project?.master_agreement;
    if (!ma || typeof ma === 'string') return null;
    return ma as any;
  });

  /** Navigate to the master client's Resume page. */
  navigateToMaster(): void {
    const ma = this.masterAgreementData();
    if (!ma?.master_client_id) return;
    // ResumeComponent reads the route param via snapshot (one-time) in ngOnInit,
    // so Angular won't re-initialize it on same-component navigation.
    // We must update the URL via the Router (not window.location.hash, which is
    // ignored now that useHash:false) and then force a full browser reload.
    const targetUrl = this.resumeData.contextModule() === 'eco'
      ? `/eco/client/${ma.master_client_id}`
      : `/uco/customer/resume/${ma.master_client_id}`;
    this.router.navigateByUrl(targetUrl)
      .then(() => window.location.reload());
  }

  data = signal<ResumeClient | null>(null);
  businessTypes = signal<{ value: string; label: string }[]>([]);
  loadingBusinessTypes = signal(false);

  readonly isCanceledClient = computed(() => {
    return this.resumeData.clientInfomationTabData()?.agreement_status === AgreementStatusEnum.Canceled;
  });

  readonly isAgreementSent = computed(() => {
    const status = this.resumeData.clientInfomationTabData()?.agreement_status;
    return status === AgreementStatusEnum.Sent || status === AgreementStatusEnum.Viewed;
  });

  /**
   * Returns true when the local-signature/upload-PDF buttons should be shown.
   * Conditions (all must be met):
   *  1. The record is still a Prospect.
   *  2. The agreement is NOT pending Zoho signature (Sent/Viewed → must Recall first).
   *  3. The agreement is NOT a draft (wizard not finished yet).
   *  4. The agreement is NOT already signed (would be Client).
   * Note: 'draft' is a raw backend string — it is not part of AgreementStatusEnum.
   */
  readonly canFinalizeAgreementLocally = computed(() => {
    const client = this.resumeData.clientInfomationTabData();
    if (!client || client.status !== CustomerTypesEnum.Prospect) {
      return false;
    }
    const status = (client.agreement_status ?? '').toLowerCase();
    const isBlocked = status === 'sent' || status === 'viewed' || status === 'draft' || status === 'signed';
    return !isBlocked;
  });

  clientForm!: FormGroup;

  private readonly fb = inject(FormBuilder);
  private readonly globalPresenter = inject(GlobalPresenter);

  constructor() {
    effect(() => {
      if(this.editClientMode()) {
        this.getBusinessTypes();
      }
    }, { allowSignalWrites: true });
  }

  ngOnInit(): void {
    this.resumeData.fetchClientInformationData().then((res: any) => this.initForm(res));
  }

  async getBusinessTypes() {
    if(this.businessTypes()?.length > 0) return;
    this.loadingBusinessTypes.set(true);
    try {
      const types:any = await this.globalPresenter.getBusinessTypesSelect();
      if (types) {
        this.businessTypes.set(
          types.map((type: any) => ({
            code: type.name,
            name: type.name
          }))
        );
      }
      this.loadingBusinessTypes.set(false);
    } catch (error) {
      this.toastr.error('Error fetching business types');
      this.loadingBusinessTypes.set(false);
    }
  }

  initForm(data: any) {
    this.clientForm = this.fb.group({
      address: [data?.address || '', Validators.required],
      address2: [data?.address2 || ''],
      city: [data?.city || '', Validators.required],
      county: [data?.county || '', Validators.required],
      state: [data?.state || '', Validators.required],
      zip_code: [data?.zip_code || '', Validators.required],
      contact_name: [data?.contact_name || '', Validators.required],
      email: [data?.email || '', [Validators.required, Validators.email]],
      phone: [data?.phone || '', Validators.required],
      referrer_name: [data?.referrer_name || ''],
      business_type: [data?.business_type || '']
    });
  }

  get control() {
    return (name: string) => this.clientForm.get(name) as FormControl;
  }

  saveClient() {
    this.modalsService.openConfirmationModal({
      title: 'Update Client',
      msg: 'Are you sure you want to update this client information?',
      onConfirm: () => {
        if (this.clientForm.invalid) {
          this.clientForm.markAllAsTouched();
          return;
        }
        const customerId = this.route.snapshot.paramMap.get('id') ?? '';
        if (!customerId) return;
        this.loadingEditClient.set(true);
        // Call the service to update client here!
        this.clientsService.update(this.clientForm.value, customerId).subscribe({
          next: (res: any) => {
            this.toastr.success('Client updated successfully');
            this.editClientMode.set(false);
            this.loadingEditClient.set(false);
            this.resumeData.invalidateClientInformation();
          },
          error: (err: any) => {
            this.toastr.error('Error updating client information');
            this.loadingEditClient.set(false);
          }
        });
      }
    });
  }

  toggleReceiptPreference(newValue: boolean) {
    const msg = newValue 
      ? 'When enabled, the client will automatically receive an email confirmation for every collection performed.'
      : 'Are you sure you want to disable email collection receipts?';
    
    this.modalsService.openConfirmationModal({
      title: 'Update Preference',
      msg: msg,
      onConfirm: () => {
        const clientId = this.resumeData.clientID();
        if (!clientId) return;
        this.resumeData.setSendCollectionReceipt(newValue);
        this.clientsService.updateCollectionReceiptPreference(clientId, newValue).subscribe({
          next: (res: any) => {
             this.toastr.success(res?.msg || 'Preference updated');
          },
          error: (err: any) => {
             this.toastr.error(err?.error?.msg || 'Error updating preference');
             this.resumeData.setSendCollectionReceipt(!newValue);
          }
        });
      }
    });
  }

  onPlaceSelected(place: any): void {
    if (place) {
      this.clientForm.patchValue({
        address: place.street_address || '',
        city: place.city || '',
        state: place.state || '',
        county: place.county || '',
        zip_code: place.zipcode || ''
      });
    }
  }

  getPDF(): void {
    const client = this.resumeData.clientInfomationTabData();
    if (!client) return;
    const project = client.project;

    if (project?.agreement?.zoho && client.agreement_status !== 'Signed') {
      this.toastr.warning('This service agreement has been sent, but has not yet been signed');
      return;
    }

    this.loadingServiceAgreement.set(true);
    this.agreementService.generateServiceAgreement(project?._id || '').subscribe({
      next: (pdfBlob: Blob) => {
        const pdfUrl = URL.createObjectURL(pdfBlob);
        window.open(pdfUrl, '_blank');
      },
      error: () => this.toastr.error('Error generating PDF'),
      complete: () => this.loadingServiceAgreement.set(false)
    });
  }

  openCreateServiceAgreement(): void {
    const customerId = this.route.snapshot.paramMap.get('id') ?? '';
    if (!customerId) return;

    const client = this.resumeData.clientInfomationTabData();
    const clientType = client?.client_type;

    if (clientType === ClientTypeEnum.ECO || (this.resumeData.contextModule() === 'eco' && client?.active === 'x')) {
      if (!this.permissions.hasPermission('customers', 'create')) return;
      // For eco_only clients, ident_GUI is the shared key between Clients and EcoClients collections.
      // Using ident_GUI ensures getEcoClientByIdService can find the EcoClient document
      // (the raw route param is the Clients _id, which is a different document).
      const identGui = client?.ident_GUI || customerId;
      this.modalsService.openEcoClientOnboardingWizard({ ecoClientId: identGui, convertToUco: true, startAtStep: 'servicesAgreement' })
        .then(modalRef => modalRef?.result)
        .then((res: any) => {
          if (res) {
            this.resumeData.invalidateClientInformation();
          }
        }).catch(() => { });
    } else {
      this.modalsService.openClientOnboardingWizard({ clientId: customerId, startAtStep: 'servicesAgreement' })
        .then(modalRef => modalRef?.result)
        .then((res: any) => {
          if (res) {
            this.resumeData.invalidateClientInformation();
          }
        }).catch(() => { });
    }
  }

  openUpdateServiceAgreement(): void {
    const client = this.resumeData.clientInfomationTabData();
    const project = this.resumeData.clientInfomationTabData()?.project;
    if (!project) return;
    const projectData = {
      _id: project._id ?? '',
      ident_client: client._id ?? '',
      address: client.address ?? '',
      price: project.price ?? null,
      gallons: project.gallons ?? null,
      container_size: project.container_size ?? null,
      contact_name:client.contact_name ?? '',
      special_requirements: project.special_requirements ?? '',
      sales_person: client.sales_person ?? '',
      sales_person_id: client.sales_person_id ?? '',
      client: { ...client },
      agreement_id: project.agreement_id ?? '',
      agreement: project.agreement ?? '',
      reconciliation_type: project.reconciliation_type ?? null,
      payment_method: project.payment_method ?? null,
      payment_to: project.payment_to ?? '',
    };

    this.modalsService
      .openUpdateCustomerServiceAgreementModal({ projectData })
      .subscribe((result: any) => {
        if (result) {
          this.resumeData.invalidateClientInformation(); // Recarga datos actualizados del cliente
        }
      });
  }

  cancelAgreement(): void {
    this.modalsService.openConfirmationModal({
      title: 'Cancel Agreement',
      msg: 'Are you sure you want to cancel this agreement? This action cannot be undone.',
      onConfirm: () => {
        const clientMongoId = this.resumeData.clientID();
        this.loadingServiceAgreement.set(true);

        this.agreementService.cancelAgreement(clientMongoId!).subscribe({
          next: (res: any) => {
            this.loadingServiceAgreement.set(false);
            if (res.ok) {
              this.resumeData.invalidateClientInformation();
              this.modalsService.openFeedbackSuccess({ title: 'Cancel Agreement', msg: res.msg });
            } else {
              this.modalsService.openFeedbackError({ title: 'Cancel Agreement', msg: res.msg });
            }
          },
          error: () => {
            this.loadingServiceAgreement.set(false);
            this.modalsService.openFeedbackError({ title: 'Cancel Agreement', msg: 'Error canceling the agreement. Please try again.' });
          }
        });
      }
    });
  }

  recreateAgreement(): void {
    if (!this.isCanceledClient()) {
      this.modalsService.openFeedbackError({ title: 'Recreate Agreement', msg: 'The agreement has not been canceled. You cannot recreate it.' });
      return;
    }

    this.modalsService.openConfirmationModal({
      title: 'Recreate Agreement',
      msg: 'Are you sure you want to recreate this agreement? A new service agreement will be generated.',
      onConfirm: () => {
        const clientMongoId = this.resumeData.clientID();
        this.loadingServiceAgreement.set(true);

        this.agreementService.recreateServiceAgreement(clientMongoId!).subscribe({
          next: (res: any) => {
            this.loadingServiceAgreement.set(false);
            if (res.ok) {
              this.resumeData.invalidateClientInformation();
              this.modalsService.openFeedbackSuccess({ title: 'Recreate Agreement', msg: res.msg });
            } else {
              this.modalsService.openFeedbackError({ title: 'Recreate Agreement', msg: res.msg });
            }
          },
          error: () => {
            this.loadingServiceAgreement.set(false);
            this.modalsService.openFeedbackError({ title: 'Recreate Agreement', msg: 'Error recreating the agreement. Please try again.' });
          }
        });
      }
    });
  }

  captureLocalSignature(): void {
    const client = this.resumeData.clientInfomationTabData();
    if (!client) return;

    this.modalsService
      .openUpdateCustomerAgreementSignatureModal({ clientName: client.name_client })
      .subscribe((result: any) => {
        if (!result?.status || result.isByZoho) return;

        const signature: string = result.newSignature;
        if (!signature) {
          this.modalsService.openFeedbackError({
            title: 'Local Signature',
            msg: 'No signature was captured. Please draw the signature before saving.'
          });
          return;
        }

        const clientId = this.resumeData.clientID();
        if (!clientId) return;

        this.loadingServiceAgreement.set(true);

        // TODO (Fase 0): Update URL once backend confirms endpoint for local-sign.
        this.agreementService.finalizeLocally(clientId, signature).subscribe({
          next: (res: any) => {
            this.loadingServiceAgreement.set(false);
            if (res.ok) {
              this.resumeData.invalidateClientInformation();
              this.modalsService.openFeedbackSuccess({
                title: 'Agreement Finalized',
                msg: 'Agreement finalized locally. The client has been converted to a Client.'
              });
            } else {
              this.modalsService.openFeedbackError({ title: 'Local Signature', msg: res.msg });
            }
          },
          error: () => {
            this.loadingServiceAgreement.set(false);
            this.modalsService.openFeedbackError({
              title: 'Local Signature',
              msg: 'Error finalizing agreement. Please try again.'
            });
          }
        });
      });
  }

  async uploadSignedPDF(file: File): Promise<void> {
    if (!file) return;

    const client = this.resumeData.clientInfomationTabData();
    const agreementId: string =
      client?.project?.agreement?._id ?? client?.project?.agreement_id ?? '';

    if (!agreementId) {
      this.modalsService.openFeedbackError({
        title: 'Upload Error',
        msg: 'Agreement ID not found. Please reload and try again.'
      });
      return;
    }

    this.loadingServiceAgreement.set(true);
    try {
      // PUT /fileuploads/sa-supports/{agreementId} — same pattern as agreement.component.ts:525
      const uploaded = await this.globalPresenter.uploadFile(file, 'sa-supports', agreementId, false);
      if (!uploaded) throw new Error('Upload returned false');

      const clientId = this.resumeData.clientID();
      if (!clientId) throw new Error('Client ID not found');

      // TODO (Fase 0): Confirm whether the same finalizeLocally endpoint accepts has_support:true
      // or if a separate endpoint is needed for the PDF-only path.
      this.agreementService.finalizeLocally(clientId, '', true).subscribe({
        next: (res: any) => {
          this.loadingServiceAgreement.set(false);
          if (res.ok) {
            this.resumeData.invalidateClientInformation();
            this.modalsService.openFeedbackSuccess({
              title: 'Upload Complete',
              msg: 'Signed agreement uploaded. The client has been converted to a Client.'
            });
          } else {
            this.modalsService.openFeedbackError({ title: 'Upload Error', msg: res.msg });
          }
        },
        error: () => {
          this.loadingServiceAgreement.set(false);
          this.modalsService.openFeedbackError({
            title: 'Upload Error',
            msg: 'File uploaded but error marking agreement as signed. Contact support.'
          });
        }
      });
    } catch {
      this.loadingServiceAgreement.set(false);
      this.modalsService.openFeedbackError({
        title: 'Upload Error',
        msg: 'Error uploading the signed agreement. Please try again.'
      });
    }
  }

  openUploadSignedPDFModal(): void {
    const client = this.resumeData.clientInfomationTabData();
    if (!client) return;

    this.modalsService
      .openUpdateCustomerAgreementSignatureModal({ clientName: client.name_client, mode: 'upload' })
      .subscribe((result: any) => {
        if (result?.status && result.file) {
          this.uploadSignedPDF(result.file);
        }
      });
  }

  readonly agreementTypeName = computed(() => {
    const typesName:any = {
      [AgreementTypeEnum.Adendum]: 'Adendum',
      [AgreementTypeEnum.Master]: 'Master Agreement',
      [AgreementTypeEnum.Single]: 'Single Agreement'
    };
    const type = this.resumeData.clientInfomationTabData()?.project?.typeAgreement || this.resumeData.clientInfomationTabData()?.ecoAgreement?.uco_agreement_type;
    return typesName[type] || 'N/A';
  });

  readonly paymentTypeName = computed(() => {
    const typesName:any = {
      [ReconciliationTypesEnum.FLAT_PRICE]: 'Flat Rate',
      [ReconciliationTypesEnum.PER_GALLON]: 'By Gallons',
      [ReconciliationTypesEnum.PER_POUNDS]: 'Per Pounds',
      [ReconciliationTypesEnum.PER_PERCENTAGE]: 'Per Percentage'
    };
    const type = this.resumeData.clientInfomationTabData()?.project?.reconciliation_type;
    return type ? (typesName[type] || type) : 'N/A';
  });

  readonly isPercentagePayment = computed(() => {
    return this.resumeData.clientInfomationTabData()?.project?.reconciliation_type === ReconciliationTypesEnum.PER_PERCENTAGE;
  });

  /** Format a last_updated_by timestamp for display (Eastern Time) */
  formatAuditDate(isoDate: string | undefined): string {
    if (!isoDate) return '';
    return new Date(isoDate).toLocaleString('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }
}
